import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Launch as LaunchIcon,
  HourglassEmpty as PendingIcon,
  Block as BlockIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";
import {
  Path,
  getExplorerAddressUrl,
  DEFAULT_CURRENCY,
} from "../../../../../static/Constants";
import { CommonButton, CommonChip } from "../../../../Components/ui";

const NoWinnerAddress = "0x0000000000000000000000000000000000000000";

const LotteryResultView = ({
  lottery,
  totalPrizeFormatted = "0",
  currency = DEFAULT_CURRENCY,
}) => {
  const history = useHistory();
  const [copied, setCopied] = useState(false);

  const status = Number(lottery?.status);
  const winnerAddress = lottery?.lotteryWinner;
  const hasWinner =
    winnerAddress &&
    winnerAddress !== NoWinnerAddress &&
    winnerAddress !== "" &&
    winnerAddress !== "0x0";

  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const truncatedWinner = winnerAddress
    ? `${winnerAddress.substring(0, 8)}...${winnerAddress.substring(winnerAddress.length - 6)}`
    : "No Winner Drawn";

  // 1. Result Done (Status 2)
  if (status === 2) {
    return (
      <Card
        className="buylottery-winner-card"
        sx={{
          borderRadius: borderRadiusPx.xl || "20px",
          overflow: "hidden",
          mb: 4,
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          <div className="winner-trophy-badge">
            <TrophyIcon sx={{ fontSize: 40 }} />
          </div>

          <CommonChip
            label="Result Announced"
            variant="warning"
            size="medium"
            sx={{
              fontWeight: 700,
              mb: 2,
              px: 2,
            }}
          />

          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: colors.slate[900],
              mb: 1,
            }}
          >
            {hasWinner ? "We Have a Winner!" : "Draw Completed"}
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: colors.slate[600], maxWidth: 500, mx: "auto", mb: 3 }}
          >
            {hasWinner
              ? `Congratulations to the lucky winner of ${totalPrizeFormatted} ${currency}!`
              : "This lottery draw has concluded. No winning ticket was claimed or rolled over."}
          </Typography>

          {/* Winner Wallet Box */}
          {hasWinner && (
            <Box
              sx={{
                bgcolor: "#ffffff",
                border: `1.5px solid ${colors.amber[200]}`,
                borderRadius: borderRadiusPx.lg,
                p: 2.5,
                maxWidth: 520,
                mx: "auto",
                mb: 4,
                boxShadow: "0 4px 12px rgba(245, 158, 11, 0.1)",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: colors.slate[500],
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  display: "block",
                  mb: 0.5,
                }}
              >
                Winning Wallet Address
              </Typography>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={1}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: colors.slate[900],
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                  }}
                >
                  {truncatedWinner}
                </Typography>

                <Tooltip title={copied ? "Copied!" : "Copy Address"}>
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(winnerAddress)}
                    sx={{ color: copied ? colors.emerald[600] : colors.slate[500] }}
                  >
                    {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>

                {getExplorerAddressUrl(winnerAddress) && (
                  <Tooltip title="View on Block Explorer">
                    <IconButton
                      size="small"
                      component="a"
                      href={getExplorerAddressUrl(winnerAddress)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: colors.primary.main }}
                    >
                      <LaunchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          )}

          {/* Action to browse more lotteries */}
          <Stack direction="row" justifyContent="center" spacing={2}>
            <CommonButton
              variant="primary"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => history.push(Path.allLotteries || "/all-lotteries")}
              label="Explore Active Lotteries"
              sx={{
                px: 3.5,
                py: 1.2,
              }}
            />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  // 2. Closed / Pending Draw (Status 1 or 6)
  if (status === 1 || status === 6) {
    return (
      <Card
        sx={{
          borderRadius: borderRadiusPx.xl,
          border: `1.5px solid ${colors.slate[200]}`,
          bgcolor: colors.slate[50],
          textAlign: "center",
          p: { xs: 3, md: 5 },
          mb: 4,
        }}
      >
        <CardContent>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: colors.blue[50],
              color: colors.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <PendingIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: colors.slate[900], mb: 1 }}>
            Draw in Progress
          </Typography>
          <Typography variant="body2" sx={{ color: colors.slate[500], maxWidth: 440, mx: "auto", mb: 3 }}>
            Ticket sales for this lottery have closed. The smart contract is processing on-chain randomness via Chainlink VRF. Results will be announced shortly.
          </Typography>
          <CommonButton
            variant="outlined"
            onClick={() => history.push(Path.allLotteries || "/all-lotteries")}
            label="Back to All Lotteries"
          />
        </CardContent>
      </Card>
    );
  }

  // 3. Fallback / Lottery Not Available
  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.xl,
        border: `1.5px solid ${colors.slate[200]}`,
        bgcolor: colors.slate[50],
        textAlign: "center",
        p: { xs: 3, md: 5 },
        mb: 4,
      }}
    >
      <CardContent>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            bgcolor: colors.red[50],
            color: colors.red[500],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <BlockIcon sx={{ fontSize: 36 }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: colors.slate[900], mb: 1 }}>
          Lottery Not Available
        </Typography>
        <Typography variant="body2" sx={{ color: colors.slate[500], maxWidth: 440, mx: "auto", mb: 3 }}>
          This lottery is currently inactive or not available for new ticket purchases.
        </Typography>
        <CommonButton
          variant="primary"
          onClick={() => history.push(Path.allLotteries || "/all-lotteries")}
          label="Browse Active Lotteries"
        />
      </CardContent>
    </Card>
  );
};

export default LotteryResultView;
